import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import TextField from "@components/ui/textfields/TextField";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import DatePicker from "@components/ui/selection/DatePicker";
import { isoToSeconds, secondsToIso } from "@utils/isoDate";
import { role_dict } from "@screens/sampleData";
import { useState } from "react";
import { invitationsApi } from "@services/api/invitations";
import { showNotification } from "context/notification.service";
import { useDataStore } from "@store/user_org";

const colorPalette = [
  "#A5B4FC", "#818CF8", "#C4B5FD", "#A78BFA", "#F9A8D4",
  "#FDA4AF", "#FCA5A5", "#FDBA74", "#FCD34D", "#FDE047",
  "#BEF264", "#86EFAC", "#6EE7B7", "#5EEAD4", "#67E8F9",
  "#7DD3FC", "#93C5FD", "#CBD5E1", "#94A3B8", "#D6D3D1",
  "#A8A29E",
];

export const AddMemberModal = ({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const userData = useDataStore((state) => state.userData);
  const fetchOrgMembers = useDataStore((state) => state.fetchOrgMembers);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState<string>(Object.keys(role_dict)[0]);
  const [accessType, setAccessType] = useState<string>("MEMBER");
  const [selectedColor, setSelectedColor] = useState<string>(colorPalette[0]);
  const [stagiaire, setStagiaire] = useState(false);
  const [stagiaireFrom, setStagiaireFrom] = useState("");
  const [stagiaireTo, setStagiaireTo] = useState("");
  const isCompagnon = ["mecanicien", "carrossier", "chef_atelier"].includes(role);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A stagiaire doesn't need a mailbox: without e-mail he is added directly, with no login.
  const asStagiaire = isCompagnon && stagiaire;
  const directStagiaire = asStagiaire && !email.trim();

  const handleSend = async () => {
    if (directStagiaire) {
      if (!name.trim()) {
        setError("Le nom est requis");
        return;
      }
      setIsSending(true);
      setError(null);
      try {
        await invitationsApi.createStagiaire({
          name: name.trim(),
          role,
          phone_number: phoneNumber || "",
          color_tag: selectedColor,
          stagiaire_from: stagiaireFrom,
          stagiaire_to: stagiaireTo,
        });
        showNotification({ type: "success", message: `${name.trim()} ajouté comme stagiaire` });
        if (userData?.org_id) await fetchOrgMembers(userData.org_id);
        onSuccess?.();
        onClose();
      } catch (err) {
        console.error("Failed to create stagiaire:", err);
        setError("Échec de l'ajout du stagiaire");
        showNotification({ type: "error", message: "Échec de l'ajout du stagiaire" });
      } finally {
        setIsSending(false);
      }
      return;
    }

    if (!email) {
      setError("L'adresse e-mail est requise");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Veuillez entrer une adresse e-mail valide");
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      await invitationsApi.create({
        invitee_email: email,
        invitee_name: name || undefined,
        invitation_type: "web",
        guest: false, // Full org member
        expires_in_hours: 168, // 7 days
        context: {
          role,
          access_type: accessType,
          color_tag: selectedColor,
          phone_number: phoneNumber || "",
          ...(asStagiaire
            ? { stagiaire: true, stagiaire_from: stagiaireFrom, stagiaire_to: stagiaireTo }
            : {}),
        },
      });

      showNotification({
        type: "success",
        message: `Invitation envoyée à ${email}`,
      });

      // Refresh members list so the new pending member appears immediately
      if (userData?.org_id) {
        await fetchOrgMembers(userData.org_id);
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      console.error("Failed to send invitation:", err);
      const errorMessage = "Échec de l'envoi de l'invitation";
      setError(errorMessage);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <BaseModal
      title="Ajouter un membre"
      startIcon={<Icon1 name="plus" size={20} />}
      onClose={onClose}
    >
      <div className="bg-neutral-100 flex flex-col gap-2 p-4">
        {/* Basic Information */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Informations du membre</div>
          <TextField
            label={asStagiaire ? "Adresse e-mail (optionnelle pour un stagiaire)" : "Adresse e-mail *"}
            value={email}
            onChange={(value) => setEmail(value)}
            placeholder="marie.curie@example.com"
            type="email"
          />
          <TextField
            label={directStagiaire ? "Nom complet *" : "Nom complet"}
            value={name}
            onChange={(value) => setName(value)}
            placeholder="Marie Curie"
          />
          <TextField
            label="Numéro de téléphone (optionnel)"
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value)}
            placeholder="06 12 34 56 78"
            type="phone"
          />
        </div>

        {/* Role and Access */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Rôle et permissions</div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <DropDown
                options={Object.entries(role_dict).map(([key, value]) => ({
                  value: key,
                  label: value,
                }))}
                label="Rôle"
                value={role}
                onSelect={(value) => setRole(String(value))}
              />
            </div>
            <div className="flex-1">
              <DropDown
                options={[
                  { value: "MEMBER", label: "Membre" },
                  { value: "ADMIN", label: "Manager" },
                ]}
                label="Niveau d'accès"
                value={accessType}
                onSelect={(value) => setAccessType(String(value))}
              />
            </div>
          </div>
          {isCompagnon && (
            <>
              <CheckBoxInput
                label="Stagiaire : visible au planning atelier, hors prise de RDV"
                checked={stagiaire}
                onChange={(e) => setStagiaire(!!e.target.checked)}
              />
              {stagiaire && (
                <>
                  <div className="flex flex-row gap-2">
                    <div className="flex-1">
                      <DatePicker
                        label="Du"
                        seconds={isoToSeconds(stagiaireFrom)}
                        onChange={(sec) => setStagiaireFrom(secondsToIso(sec))}
                      />
                    </div>
                    <div className="flex-1">
                      <DatePicker
                        label="Au"
                        seconds={isoToSeconds(stagiaireTo)}
                        minDateSeconds={isoToSeconds(stagiaireFrom)}
                        onChange={(sec) => setStagiaireTo(secondsToIso(sec))}
                      />
                    </div>
                  </div>
                  <div className="text-h4 text-neutral-500">
                    Sans adresse e-mail, le stagiaire est ajouté directement, sans invitation ni accès à Carvis.
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Color Tag Selection */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Couleur du drapeau</div>
          <div className="flex flex-wrap gap-1">
            {colorPalette.map((color) => (
              <button
                key={color}
                onClick={() => setSelectedColor(color)}
                className={`w-8 h-8 rounded-full transition-all hover:scale-110 ${
                  selectedColor === color
                    ? "ring-2 ring-offset-1 ring-black"
                    : "hover:ring-2 hover:ring-offset-1 hover:ring-neutral-300"
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-h4 text-red-600">
            {error}
          </div>
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose} disabled={isSending}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={isSending ? "Envoi en cours..." : directStagiaire ? "Ajouter le stagiaire" : "Envoyer l'invitation"}
          onClick={handleSend}
          disabled={isSending}
        />
      </div>
    </BaseModal>
  );
};
